import { cn } from "@/utils";
import { Eye, EyeOff } from "lucide-react";
import { controlBase } from "./field.styles";
import { useLarguraDoAfixo } from "./useLarguraDoAfixo";
import {
  forwardRef,
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";

/**
 * Campos de formulário do Design System. O select vive em `Select.tsx`, porque
 * ele não embrulha um elemento nativo — a lista é desenhada por nós.
 *
 * Três decisões que valem para todos eles:
 * 1. `text-ds-body` (16px) no controle — abaixo disso o Safari do iPhone dá
 *    zoom sozinho ao focar, e o usuário perde o contexto da tela.
 * 2. Altura mínima 48px: alvo de toque confortável de pé, no balcão.
 * 3. Erro e dica são ligados ao controle por `aria-describedby`, e o erro
 *    também marca `aria-invalid` — cor vermelha sozinha não comunica nada a
 *    quem não enxerga cor.
 */

export type FieldShellProps = {
  label: string;
  /** Some o rótulo visualmente mas mantém para leitor de tela. Use com parcimônia. */
  hideLabel?: boolean;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  controlId: string;
  hintId: string;
  errorId: string;
  className?: string;
  children: ReactNode;
};

export const FieldShell = ({
  label,
  hideLabel,
  hint,
  error,
  required,
  controlId,
  hintId,
  errorId,
  className,
  children,
}: FieldShellProps) => (
  <div className={cn("flex flex-col gap-1.5", className)}>
    <label
      htmlFor={controlId}
      className={cn(
        "font-ds-sans text-ds-small text-ds-ink-700 font-medium",
        hideLabel && "sr-only"
      )}
    >
      {label}
      {required ? (
        <span className="text-ds-danger-700 ml-0.5" aria-hidden="true">
          *
        </span>
      ) : hideLabel ? null : (
        // Com o rótulo oculto, "(opcional)" seria a única coisa que o leitor de
        // tela acrescentaria ao nome do campo — o asterisco é `aria-hidden`,
        // este texto não. E ele carrega uma semântica de formulário que um
        // campo de busca não tem: ninguém deixa de preencher uma busca.
        <span className="text-ds-ink-600 ml-1.5 font-normal">(opcional)</span>
      )}
    </label>

    {children}

    {hint && !error ? (
      <p id={hintId} className="font-ds-sans text-ds-caption text-ds-ink-600">
        {hint}
      </p>
    ) : null}

    {error ? (
      <p
        id={errorId}
        role="alert"
        className="font-ds-sans text-ds-caption text-ds-danger-700 font-medium"
      >
        {error}
      </p>
    ) : null}
  </div>
);

type SharedFieldProps = {
  label: string;
  hideLabel?: boolean;
  hint?: ReactNode;
  error?: string;
  containerClassName?: string;
};

export interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "prefix">,
    SharedFieldProps {
  /** Ícone ou texto fixo à esquerda (ex.: "R$"). Decorativo — não recebe foco.
   *  `prefix` aqui sobrepõe o atributo HTML homônimo (de RDFa), que ninguém usa. */
  prefix?: ReactNode;
  suffix?: ReactNode;
  /**
   * Liga o botão de mostrar/ocultar num campo `type="password"`.
   *
   * Sem ele, quem digita a senha no celular não tem como conferir o que
   * escreveu — e senha errada por uma tecla é o tipo de erro que só aparece
   * depois de enviar. O botão é `<button>` de verdade, com 44px de alvo e
   * `aria-pressed`, e não um ícone decorativo: `suffix` é `aria-hidden` e
   * `pointer-events-none` de propósito, então não serviria aqui.
   */
  passwordToggle?: boolean;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      label,
      hideLabel,
      hint,
      error,
      containerClassName,
      className,
      prefix,
      suffix,
      passwordToggle,
      type,
      id,
      required,
      style,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const [senhaVisivel, setSenhaVisivel] = useState(false);
    const medidaDoPrefixo = useLarguraDoAfixo(prefix);
    const medidaDoSufixo = useLarguraDoAfixo(suffix);

    // O botão só existe em campo de senha: ligado num campo comum, ele
    // prometeria revelar algo que já está à vista.
    const mostrarBotaoDeSenha = passwordToggle && type === "password";
    const tipoDoCampo = mostrarBotaoDeSenha && senhaVisivel ? "text" : type;
    const controlId = id ?? generatedId;
    const hintId = `${controlId}-hint`;
    const errorId = `${controlId}-error`;

    return (
      <FieldShell
        label={label}
        hideLabel={hideLabel}
        hint={hint}
        error={error}
        required={required}
        controlId={controlId}
        hintId={hintId}
        errorId={errorId}
        className={containerClassName}
      >
        <div className="relative flex items-center">
          {prefix ? (
            <span
              ref={medidaDoPrefixo.ref}
              aria-hidden="true"
              className="font-ds-sans text-ds-body text-ds-ink-600 pointer-events-none absolute left-3 whitespace-nowrap"
            >
              {prefix}
            </span>
          ) : null}
          <input
            ref={ref}
            id={controlId}
            type={tipoDoCampo}
            required={required}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : hint ? hintId : undefined}
            className={cn(
              controlBase,
              "min-h-12 px-3",
              prefix && "pl-10",
              suffix && "pr-10",
              mostrarBotaoDeSenha && "pr-14",
              className
            )}
            // O espaço do afixo é medido, não estimado: com o `pl-10` fixo,
            // um prefixo longo como "/blog/cluster/" ficava por baixo do texto
            // digitado. Vai no `style` porque a largura só existe em tempo de
            // execução; sem medida (1º render, jsdom) vale a classe acima.
            style={{
              paddingLeft: medidaDoPrefixo.espaco,
              // Com o botão de senha, o espaço da direita é dele (`pr-14`).
              paddingRight: mostrarBotaoDeSenha
                ? undefined
                : medidaDoSufixo.espaco,
              ...style,
            }}
            {...props}
          />

          {mostrarBotaoDeSenha ? (
            <button
              type="button"
              onClick={() => setSenhaVisivel(atual => !atual)}
              aria-pressed={senhaVisivel}
              aria-controls={controlId}
              aria-label={senhaVisivel ? "Ocultar a senha" : "Mostrar a senha"}
              className="text-ds-ink-600 hover:text-ds-ink-900 focus-visible:ring-ds-focus focus-visible:ring-offset-ds-surface rounded-ds-sm absolute right-1 inline-flex size-11 cursor-pointer items-center justify-center transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none"
            >
              {senhaVisivel ? (
                <EyeOff aria-hidden="true" className="size-5" />
              ) : (
                <Eye aria-hidden="true" className="size-5" />
              )}
            </button>
          ) : null}
          {suffix ? (
            <span
              ref={medidaDoSufixo.ref}
              aria-hidden="true"
              className="font-ds-sans text-ds-body text-ds-ink-600 pointer-events-none absolute right-3 whitespace-nowrap"
            >
              {suffix}
            </span>
          ) : null}
        </div>
      </FieldShell>
    );
  }
);
TextField.displayName = "TextField";

export interface TextAreaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement>,
    SharedFieldProps {
  /**
   * Some com o contador. Só use quando o campo não tiver limite de verdade —
   * saber quanto ainda cabe é parte de escrever a mensagem, não enfeite.
   */
  hideCounter?: boolean;
}

/**
 * Área de texto com contador.
 *
 * O contador fica dentro do campo, no canto inferior direito, e o textarea ganha
 * espaço embaixo para o texto nunca passar por baixo dele. Ele para a 16px da
 * borda, e não colado, porque ali fica a alça de redimensionar do navegador —
 * que não dá para estilizar nem mover.
 *
 * Perto do limite ele muda de cor: avisa antes de o usuário perder o que
 * escreveu, em vez de simplesmente parar de aceitar teclas.
 */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      hideLabel,
      hint,
      error,
      containerClassName,
      className,
      id,
      required,
      hideCounter = false,
      maxLength,
      value,
      defaultValue,
      onChange,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const controlId = id ?? generatedId;
    const hintId = `${controlId}-hint`;
    const errorId = `${controlId}-error`;
    const counterId = `${controlId}-counter`;

    const controlado = value !== undefined;
    const [valorInterno, setValorInterno] = useState(
      String(defaultValue ?? "")
    );
    const texto = controlado ? String(value ?? "") : valorInterno;

    // Campo não controlado (o `register` do react-hook-form) recebe valor sem
    // passar pelo `onChange`: o `reset`/`defaultValues` escreve direto no DOM.
    // Sem ler o DOM de volta, o contador ficava em "0/300" com o texto do
    // cluster já carregado no campo. Roda a cada render porque não há evento
    // para escrita programática — e o `reset` sempre re-renderiza o formulário.
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    // Sem lista de dependências de propósito; não entra em laço porque só
    // grava quando o DOM e o estado divergem.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(() => {
      if (controlado || !textareaRef.current) return;
      const valorNoDom = textareaRef.current.value;
      if (valorNoDom !== valorInterno) setValorInterno(valorNoDom);
    });

    const ligarRef = useCallback(
      (elemento: HTMLTextAreaElement | null) => {
        textareaRef.current = elemento;
        if (typeof ref === "function") ref(elemento);
        else if (ref) ref.current = elemento;
      },
      [ref]
    );
    const usados = texto.length;

    const mostrarContador = !hideCounter && maxLength !== undefined;
    const restantes = maxLength === undefined ? Infinity : maxLength - usados;
    const noLimite = restantes <= 0;
    const perto = restantes <= 20 && !noLimite;

    return (
      <FieldShell
        label={label}
        hideLabel={hideLabel}
        hint={hint}
        error={error}
        required={required}
        controlId={controlId}
        hintId={hintId}
        errorId={errorId}
        className={containerClassName}
      >
        <div className="relative">
          <textarea
            ref={ligarRef}
            id={controlId}
            required={required}
            rows={props.rows ?? 4}
            maxLength={maxLength}
            value={controlado ? value : undefined}
            defaultValue={controlado ? undefined : defaultValue}
            onChange={event => {
              if (!controlado) setValorInterno(event.target.value);
              onChange?.(event);
            }}
            aria-invalid={error ? true : undefined}
            aria-describedby={
              [
                error ? errorId : hint ? hintId : null,
                mostrarContador ? counterId : null,
              ]
                .filter(Boolean)
                .join(" ") || undefined
            }
            className={cn(
              controlBase,
              "min-h-24 resize-y px-3 py-2.5",
              mostrarContador && "pb-8",
              className
            )}
            {...props}
          />

          {mostrarContador ? (
            <>
              <span
                aria-hidden="true"
                className={cn(
                  "ds-numeric font-ds-sans text-ds-caption pointer-events-none absolute right-4 bottom-2",
                  noLimite
                    ? "text-ds-danger-700 font-semibold"
                    : perto
                      ? "text-ds-warning-700 font-medium"
                      : "text-ds-ink-600"
                )}
              >
                {usados}/{maxLength}
              </span>

              {/* O leitor de tela recebe o limite uma vez, no repouso, e só volta
                  a ser avisado quando a folga aperta. Anunciar a cada tecla
                  tornaria o campo inutilizável para quem escuta a página. */}
              <span id={counterId} className="sr-only">
                Máximo de {maxLength} caracteres.
              </span>
              <span role="status" aria-live="polite" className="sr-only">
                {noLimite
                  ? "Limite de caracteres atingido."
                  : perto
                    ? `Restam ${restantes} caracteres.`
                    : ""}
              </span>
            </>
          ) : null}
        </div>
      </FieldShell>
    );
  }
);
TextArea.displayName = "TextArea";
